/**
 * Heikin Ashi chart STYLE (Beta).
 *
 * Registered as an internal klinecharts indicator — the TPO precedent — rather
 * than by transforming the bars handed to the chart. Every indicator klinecharts
 * runs reads the chart's own data list, so transforming that list would make
 * the EMA, the S/R detector and the live-bar merge all see smoothed prices. Keeping
 * raw bars in the list and PAINTING the averaged candles in `draw` is the only
 * route where "indicators keep computing on real prices" holds.
 *
 * Mechanics borrowed from two neighbours:
 *  - `tpoIndicator.ts` for the registration shape: `series: 'price'`, figures
 *    declared ONLY to feed the price axis (the native series is forced to an
 *    `area` type whose axis fit reads closes alone), an empty tooltip source so
 *    it never appears in the legend, and `registerOnce`.
 *  - `htfIndicator.ts` for candle painting: `xAxis.convertToPixel(index)` for x,
 *    `barSpace` for the body width, `fillRect` / `strokeRect` for bodies and wicks.
 *
 * `zLevel: 0`, not TPO's -1: a negative level composites with `destination-over`,
 * which lands BEHIND the grid. These are the primary series and paint on top.
 * Known v1 limit: among zLevel-0 indicators the stable sort keeps creation order,
 * so a picker indicator created before this one is painted over by opaque bodies.
 *
 * `calc` reruns over the FULL list on every klinecharts data mutation (prepend,
 * append, `updateData`), so the recursion always re-seeds from index 0 and the
 * seed's influence halves per bar — no memo, no drift on scroll-back.
 */

import { registerIndicator, type DeepPartial, type KLineData, type Styles } from 'klinecharts'
import {
  DEFAULT_SETTINGS,
  HEIKIN_ASHI_HOLLOW_TYPE,
  HEIKIN_ASHI_TYPE,
  type ChartSettings,
} from '../components/chartSettings'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import { chartColorDefaults, effectiveColor, type ThemedColorKey } from './themedColors'

export const HEIKIN_ASHI_INDICATOR_NAME = 'HEIKIN_ASHI'

/** Doji (open === close) is neutral, not bearish — the same gray the native
 *  candle style uses (`noChangeColor` in useChartStyles). */
const NO_CHANGE_COLOR = '#758696'
/** Floor for the body height so a doji stays visible. */
const MIN_BODY_PX = 1

export interface HeikinAshiPoint {
  o: number
  h: number
  l: number
  c: number
}

/**
 * The standard recursion:
 *   c[i] = (open + high + low + close) / 4
 *   o[0] = (open0 + close0) / 2 ;  o[i] = (o[i-1] + c[i-1]) / 2
 *   h[i] = max(high, o[i], c[i]) ;  l[i] = min(low, o[i], c[i])
 * Pure and O(n); the axis figures (h, l) and the draw both read this result.
 */
export function computeHeikinAshi(
  list: readonly Pick<KLineData, 'open' | 'high' | 'low' | 'close'>[],
): HeikinAshiPoint[] {
  const out: HeikinAshiPoint[] = []
  let prevO = 0
  let prevC = 0
  for (let i = 0; i < list.length; i++) {
    const b = list[i]
    const c = (b.open + b.high + b.low + b.close) / 4
    const o = i === 0 ? (b.open + b.close) / 2 : (prevO + prevC) / 2
    const h = Math.max(b.high, o, c)
    const l = Math.min(b.low, o, c)
    out.push({ o, h, l, c })
    prevO = o
    prevC = c
  }
  return out
}

/** Everything the painter needs, resolved once per style change on the React
 *  side and handed over via `indicator.extendData.heikin`. Booleans, not
 *  transparent colours: the draw skips the geometry, which is cheaper and
 *  avoids seam artefacts from transparent strokes. */
export interface HeikinAshiStyle {
  upColor: string
  downColor: string
  upBorderColor: string
  downBorderColor: string
  upWickColor: string
  downWickColor: string
  noChangeColor: string
  showBorder: boolean
  showWicks: boolean
  hollow: boolean
}

/**
 * Resolve the themed candle colours the same way `useChartStyles` does for the
 * native series (theme defaults, then the user's explicit picks), so Heikin Ashi
 * bodies match whatever the Candles style would paint.
 */
export function heikinAshiStyleOf(
  settings: ChartSettings,
  /** The chart's `theme` prop: a klinecharts style object, or a preset name
   *  (string) that carries no colours and resolves to the built-in defaults. */
  theme: unknown,
  chartType: string,
): HeikinAshiStyle {
  const t = theme && typeof theme === 'object' ? (theme as DeepPartial<Styles>) : undefined
  const defaults = chartColorDefaults(t, settings)
  const eff = (key: ThemedColorKey): string =>
    effectiveColor(key, settings[key], defaults, settings.customizedColors)
  return {
    upColor: eff('candleUpColor'),
    downColor: eff('candleDownColor'),
    upBorderColor: eff('candleBorderUpColor'),
    downBorderColor: eff('candleBorderDownColor'),
    upWickColor: eff('wickUpColor'),
    downWickColor: eff('wickDownColor'),
    noChangeColor: NO_CHANGE_COLOR,
    showBorder: settings.showCandleBorder !== false,
    showWicks: settings.showCandleWicks !== false,
    hollow: chartType === HEIKIN_ASHI_HOLLOW_TYPE,
  }
}

/** Read the style off `extendData`; a missing or partial payload (the indicator
 *  can paint before React has pushed a style) falls back to the defaults. */
export function heikinAshiStyleFrom(extendData: unknown): HeikinAshiStyle {
  const base = heikinAshiStyleOf(DEFAULT_SETTINGS, undefined, HEIKIN_ASHI_TYPE)
  const ext = (extendData as { heikin?: Partial<HeikinAshiStyle> } | undefined)?.heikin
  return ext ? { ...base, ...ext } : base
}

/** The subset of CanvasRenderingContext2D the painter touches (testable with a
 *  recording stub). */
export interface HeikinAshiCtx {
  fillStyle: string | CanvasGradient | CanvasPattern
  strokeStyle: string | CanvasGradient | CanvasPattern
  lineWidth: number
  fillRect: (x: number, y: number, w: number, h: number) => void
  strokeRect: (x: number, y: number, w: number, h: number) => void
  save: () => void
  restore: () => void
  setLineDash: (segments: number[]) => void
}

export interface HeikinAshiDrawParams {
  ctx: HeikinAshiCtx
  visibleRange: { from: number; to: number }
  yAxis: { convertToPixel: (value: number) => number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  barSpace: { gapBar: number; halfGapBar: number }
  indicator: { result?: HeikinAshiPoint[]; extendData?: unknown }
}

/**
 * Paint the averaged candles for the visible range. Geometry follows the native
 * candle bar: a 1px wick at the bar centre from high to low, a body `gapBar`
 * wide from open to close. Solid: fill then optional border stroke. Hollow:
 * stroke only, with the wick split above and below the body so it does not
 * cross the empty interior. Returns `true` so klinecharts does NOT also plot
 * the `h` / `l` figures as lines.
 */
export function drawHeikinAshi(p: HeikinAshiDrawParams): boolean {
  const { ctx, visibleRange, yAxis, xAxis, barSpace, indicator } = p
  const result = indicator.result ?? []
  if (result.length === 0) return true
  const style = heikinAshiStyleFrom(indicator.extendData)
  const from = Math.max(0, Math.floor(visibleRange.from))
  const to = Math.min(result.length, Math.ceil(visibleRange.to))

  ctx.save()
  ctx.setLineDash([])
  ctx.lineWidth = 1
  for (let i = from; i < to; i++) {
    const ha = result[i]
    if (!ha) continue
    const x = xAxis.convertToPixel(i)
    const yO = yAxis.convertToPixel(ha.o)
    const yC = yAxis.convertToPixel(ha.c)
    const yH = yAxis.convertToPixel(ha.h)
    const yL = yAxis.convertToPixel(ha.l)
    if (![x, yO, yC, yH, yL].every(Number.isFinite)) continue

    const up = ha.c > ha.o
    const down = ha.c < ha.o
    const body = up ? style.upColor : down ? style.downColor : style.noChangeColor
    const border = up ? style.upBorderColor : down ? style.downBorderColor : style.noChangeColor
    const wick = up ? style.upWickColor : down ? style.downWickColor : style.noChangeColor

    const bodyTop = Math.min(yO, yC)
    const bodyH = Math.max(Math.abs(yC - yO), MIN_BODY_PX)
    const bodyBottom = bodyTop + bodyH
    const bodyX = x - barSpace.halfGapBar
    const bodyW = Math.max(barSpace.gapBar, 1)

    if (style.showWicks) {
      ctx.fillStyle = wick
      if (style.hollow) {
        // Above and below the body only, so the hollow interior stays clear.
        if (yH < bodyTop) ctx.fillRect(x - 0.5, yH, 1, bodyTop - yH)
        if (yL > bodyBottom) ctx.fillRect(x - 0.5, bodyBottom, 1, yL - bodyBottom)
      } else {
        ctx.fillRect(x - 0.5, Math.min(yH, yL), 1, Math.abs(yL - yH))
      }
    }
    if (style.hollow) {
      ctx.strokeStyle = border
      ctx.strokeRect(bodyX, bodyTop, bodyW, bodyH)
    } else {
      ctx.fillStyle = body
      ctx.fillRect(bodyX, bodyTop, bodyW, bodyH)
      if (style.showBorder) {
        ctx.strokeStyle = border
        ctx.strokeRect(bodyX, bodyTop, bodyW, bodyH)
      }
    }
  }
  ctx.restore()
  return true
}

/** Register the internal Heikin Ashi chart-style indicator with klinecharts (once). */
export const registerHeikinAshiIndicator = registerOnce(() => {
  registerIndicator({
    name: HEIKIN_ASHI_INDICATOR_NAME,
    shortName: 'HA',
    // 'price', not 'volume' — the precision trap TPO and VPVR document.
    series: 'price' as never,
    calcParams: [],
    /**
     * Figures exist ONLY to feed the price axis — `draw` returns true, so
     * klinecharts never renders them. The native series is forced to `area`
     * for this style, and klinecharts' `calcRange` fits an area series to its
     * CLOSES; the averaged high/low can sit outside the raw range (a gap bar's
     * HA open is the previous midpoint), so without these the tallest candles
     * clip. `shouldOhlc` is deliberately not set: on the candle pane it is
     * ignored (see tpoIndicator.ts).
     */
    figures: [
      { key: 'h', title: '', type: 'line' },
      { key: 'l', title: '', type: 'line' },
    ],
    zLevel: 0,
    calc: (dataList: KLineData[]) => computeHeikinAshi(dataList),
    draw: ((p: HeikinAshiDrawParams) => drawHeikinAshi(p)) as never,
    // Never in the legend: it is the chart style, not an overlay.
    createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }),
  } as IndicatorTemplateParam)
})
